import React, { useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { getSignupRequests, approveSignupRequest, declineSignupRequest } from '../api';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';

// Settings > Customers, above the customer list: companies that asked for a
// workspace on the public sign-up page. Approving one creates the customer
// and invites the person who asked as its first admin. Declining only
// records the decision; no email is sent either way except the invite.

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
const TEAM_LABELS = { '1-5': '1 to 5', '6-20': '6 to 20', '21-50': '21 to 50', '51+': 'More than 50' };

const when = (iso) => {
  if (!iso) return '';
  const text = String(iso);
  const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : text.replace(' ', 'T') + 'Z');
  return Number.isNaN(date.getTime()) ? text : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// onApproved(customer, admin, request): the new customer and its first admin
// (with invite: { sent } or { url } when email isn't set up).
export default function SignupRequests({ onApproved }) {
  const [requests, setRequests] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const [approving, setApproving] = useState(null); // the request in the approve dialog
  const [form, setForm] = useState({ name: '', slug: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const [declineTarget, setDeclineTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSignupRequests('pending')
      .then((res) => { if (!cancelled) setRequests(Array.isArray(res.data?.requests) ? res.data.requests : []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Could not load sign-up requests'); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const openApprove = (r) => {
    setForm({ name: r.company, slug: r.suggested_slug || '' });
    setFormError('');
    setApproving(r);
  };

  const closeApprove = () => { if (!saving) setApproving(null); };

  const approve = async (e) => {
    e.preventDefault();
    const payload = { name: form.name.trim(), slug: form.slug.trim().toLowerCase() };
    if (!payload.name) { setFormError('Enter the company name.'); return; }
    if (!SLUG_PATTERN.test(payload.slug)) {
      setFormError('The customer ID can use 1 to 40 lowercase letters, digits or hyphens, and cannot start or end with a hyphen.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const res = await approveSignupRequest(approving.id, payload);
      const { customer, admin } = res.data || {};
      setRequests((list) => list.filter((r) => r.id !== approving.id));
      const request = approving;
      setApproving(null);
      if (onApproved) onApproved(customer, admin, request);
    } catch (err) {
      setFormError(err.response?.data?.error || 'Could not approve this request');
    } finally {
      setSaving(false);
    }
  };

  const confirmDecline = async () => {
    const target = declineTarget;
    setDeclineTarget(null);
    if (!target) return;
    setBusyId(target.id);
    setError('');
    try {
      await declineSignupRequest(target.id);
      setRequests((list) => list.filter((r) => r.id !== target.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not decline this request');
    } finally {
      setBusyId(null);
    }
  };

  if (!loaded || (!error && requests.length === 0)) return null;

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="card-header">
        <div>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserPlus size={18} /> Sign-up requests
            {requests.length > 0 && <span className="badge badge-warning">{requests.length}</span>}
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Companies that asked for a workspace. Approving creates it and invites the person who asked as its first admin.
          </p>
        </div>
      </div>
      {error && (
        <div className="card-body" style={{ paddingBottom: 0 }}>
          <div className="alert alert-error" role="alert">{error}</div>
        </div>
      )}
      {requests.length > 0 && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Company</th>
                <th scope="col">Requested by</th>
                <th scope="col">Team</th>
                <th scope="col">Received</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.company}</div>
                    {r.message && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', maxWidth: '360px' }}>{r.message}</div>}
                  </td>
                  <td>
                    <div>{r.name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{r.email}</div>
                    {r.email_has_login && (
                      <div style={{ fontSize: '13px', color: 'var(--warning, #d97706)' }}>This email already has a login.</div>
                    )}
                  </td>
                  <td>{TEAM_LABELS[r.team_size] || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(r.created_at)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" className="btn btn-primary btn-sm" disabled={busyId === r.id} onClick={() => openApprove(r)}
                        aria-label={`Approve ${r.company}`}>
                        Approve
                      </button>
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === r.id} onClick={() => setDeclineTarget(r)}
                        aria-label={`Decline ${r.company}`}>
                        Decline
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={!!approving} onClose={closeApprove} title="Approve sign-up">
        {approving && (
          <form onSubmit={approve} noValidate>
            <div className="modal-body">
              {formError && <div className="alert alert-error" role="alert">{formError}</div>}
              <p style={{ marginTop: 0 }}>
                This creates a workspace for {approving.company} and invites {approving.name} ({approving.email}) as its first admin.
              </p>
              <div className="form-group">
                <label className="form-label" htmlFor="approve-name">Company name *</label>
                <input id="approve-name" type="text" className="form-input" value={form.name} maxLength={120}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} autoComplete="off" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="approve-slug">Customer ID *</label>
                <input id="approve-slug" type="text" className="form-input" value={form.slug} maxLength={40}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))} autoComplete="off" aria-describedby="approve-slug-hint" />
                <p id="approve-slug-hint" style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Lowercase letters, digits and hyphens. This cannot be changed later.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeApprove} disabled={saving}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating...' : 'Approve and invite'}</button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
        onConfirm={confirmDecline}
        title="Decline sign-up"
        message={declineTarget ? `Decline the request from ${declineTarget.company}? Nothing is created and no email is sent. They can ask again later.` : ''}
        confirmText="Decline"
      />
    </div>
  );
}
